import React, { useState, useEffect, useRef } from 'react';
import { auth, db, loginWithGoogle } from './firebase';
import { 
  collection, addDoc, deleteDoc, doc, query, onSnapshot, 
  updateDoc, increment, arrayUnion, getDoc, setDoc, 
  arrayRemove, serverTimestamp 
} from "firebase/firestore"; 

const ADMIN_UID = "3PTDmoXDS4gs6OJ9qsdXPQu8XgO2";

function App() {
  // --- States ---
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState({ liked: [], history: [], favorites: [] });
  const [view, setView] = useState('home'); 
  const [channelTab, setChannelTab] = useState('uploads');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortMethod, setSortMethod] = useState('newest'); 
  const [selectedFilter, setSelectedFilter] = useState("הכל");
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [openModal, setOpenModal] = useState(null); 

  const [videos, setVideos] = useState([]);
  const [stories, setStories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pendingVideos, setPendingVideos] = useState([]);
  const [pendingStories, setPendingStories] = useState([]);

  // States לטפסים
  const [videoUrl, setVideoUrl] = useState('');
  const [storyUrl, setStoryUrl] = useState('');
  const [fetchedTitle, setFetchedTitle] = useState('');
  const [targetCategory, setTargetCategory] = useState('');
  const [newCatName, setNewCatName] = useState('');
  const [editingVideoId, setEditingVideoId] = useState(null);
  const [tempCategory, setTempCategory] = useState('');
  const [editingCat, setEditingCat] = useState(null);

  // Player States
  const playerRef = useRef(null);
  const modalContentRef = useRef(null); 
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(70);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const isMobile = windowWidth < 768;
  const isLandscape = windowWidth > windowHeight;
  const isStoryMode = selectedVideo?.url?.includes('shorts') || stories.some(s => s.id === selectedVideo?.id);

  // --- Effects ---
  useEffect(() => {
    const handleResize = () => { setWindowWidth(window.innerWidth); setWindowHeight(window.innerHeight); };
    window.addEventListener('resize', handleResize);
    const unsubscribe = auth.onAuthStateChanged(async (u) => {
      setUser(u);
      if (u) {
        const userRef = doc(db, "users", u.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) setUserData(userDoc.data());
        else await setDoc(userRef, { liked: [], history: [], favorites: [] });
      }
    });
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(tag);
    }
    return () => { window.removeEventListener('resize', handleResize); unsubscribe(); };
  }, []);

  useEffect(() => {
    onSnapshot(query(collection(db, "videos")), (s) => setVideos(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    
    onSnapshot(query(collection(db, "stories")), (s) => {
      const now = Date.now();
      const filtered = s.docs.map(d => ({ id: d.id, ...d.data() })).filter(st => {
        if (!st.createdAt) return true;
        const time = st.createdAt.toMillis ? st.createdAt.toMillis() : st.createdAt;
        return (now - time) < (24 * 60 * 60 * 1000);
      });
      setStories(filtered);
    });

    onSnapshot(query(collection(db, "categories")), (s) => setCategories(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    
    if (user?.uid === ADMIN_UID) {
      onSnapshot(query(collection(db, "pending_videos")), (s) => setPendingVideos(s.docs.map(d => ({ id: d.id, ...d.data() }))));
      onSnapshot(query(collection(db, "pending_stories")), (s) => setPendingStories(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    }
  }, [user]);

  // --- Functions ---
  const extractID = (url) => {
    const reg = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=|live\/|shorts\/)([^#\&\?]*).*/;
    const match = url?.match(reg);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const handleLike = async (e, vId) => {
    e.stopPropagation();
    if (!user) return alert("התחבר קודם");
    const userRef = doc(db, "users", user.uid);
    const videoRef = doc(db, "videos", vId);
    const isLiked = (userData.liked || []).includes(vId);
    await updateDoc(userRef, { liked: isLiked ? arrayRemove(vId) : arrayUnion(vId) });
    await updateDoc(videoRef, { likes: isLiked ? increment(-1) : increment(1) });
    setUserData(p => ({ ...p, liked: isLiked ? p.liked.filter(id => id !== vId) : [...(p.liked || []), vId] }));
  };

  const approveItem = async (item, type) => {
    const { id, ...data } = item;
    const targetColl = type === 'video' ? "videos" : "stories";
    const pendColl = type === 'video' ? "pending_videos" : "pending_stories";
    await addDoc(collection(db, targetColl), { ...data, views: 0, likes: 0, createdAt: serverTimestamp() });
    await deleteDoc(doc(db, pendColl, id));
  };

  const getSortedContent = (list) => {
    let filtered = list.filter(v => 
      v.title?.toLowerCase().includes(searchTerm.toLowerCase()) && 
      (selectedFilter === "הכל" || v.category === selectedFilter)
    );
    return filtered.sort((a, b) => {
      const tA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
      const tB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
      if (sortMethod === 'newest') return tB - tA;
      if (sortMethod === 'oldest') return tA - tB;
      if (sortMethod === 'az') return a.title?.localeCompare(b.title);
      if (sortMethod === 'za') return b.title?.localeCompare(a.title);
      if (sortMethod === 'views') return (b.views || 0) - (a.views || 0);
      if (sortMethod === 'likes') return (b.likes || 0) - (a.likes || 0);
      return 0;
    });
  };

  // --- Player Effect ---
  useEffect(() => {
    if (selectedVideo && window.YT) {
      const vidId = extractID(selectedVideo.url);
      if (!vidId) return;
      if (playerRef.current) playerRef.current.destroy();
      playerRef.current = new window.YT.Player('yt-player-frame', {
        videoId: vidId,
        playerVars: { autoplay: 1, controls: 0, modestbranding: 1, rel: 0, playsinline: 1 },
        events: {
          onReady: (e) => { setDuration(e.target.getDuration()); e.target.setVolume(volume); setIsPlaying(true); },
          onStateChange: (e) => setIsPlaying(e.data === 1)
        }
      });
    }
  }, [selectedVideo]);

  useEffect(() => {
    const int = setInterval(() => {
      if (playerRef.current && isPlaying) {
        const cur = playerRef.current.getCurrentTime();
        setCurrentTime(cur);
        setProgress((cur / duration) * 100);
      }
    }, 1000);
    return () => clearInterval(int);
  }, [isPlaying, duration]);

  const VideoCard = ({ v }) => (
    <div key={v.id} style={s.videoCard} onClick={() => { setSelectedVideo(v); if(user) updateDoc(doc(db,"videos",v.id),{views: increment(1)}) }}>
      <div style={s.videoWrapper}><img src={`https://img.youtube.com/vi/${extractID(v.url)}/hqdefault.jpg`} alt="" style={s.thumbnail} /></div>
      <div style={s.videoInfo}>
        <h4 style={s.vTitle}>{v.title}</h4>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
          <div>
            {editingVideoId === v.id ? (
              <select value={tempCategory} onChange={(e) => setTempCategory(e.target.value)} onBlur={() => {updateDoc(doc(db,"videos",v.id),{category:tempCategory}); setEditingVideoId(null)}} autoFocus style={s.smallInput}>
                {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            ) : (
              <p style={s.vRabbi}>🎤 {v.category} {user?.uid === ADMIN_UID && <span onClick={(e) => {e.stopPropagation(); setEditingVideoId(v.id); setTempCategory(v.category);}} style={{cursor:'pointer'}}>✏️</span>}</p>
            )}
            <p style={s.vStats}>👁️ {v.views || 0} • ❤️ {v.likes || 0}</p>
          </div>
          <button onClick={(e) => handleLike(e, v.id)} style={{...s.gridLike, color: (userData.liked || []).includes(v.id) ? '#ff4b2b' : '#ccc'}}>❤️</button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={s.container}>
      <header style={{...s.header, height: isMobile ? '70px' : '90px'}}>
        <img src="/logo.png" alt="KosherTube" style={{height: isMobile ? '40px' : '80px', cursor:'pointer'}} onClick={() => setView('home')} />
        <div style={s.actionButtons}>
          {user && <button onClick={() => setOpenModal('video')} style={s.createBtn}>וידאו 🎥</button>}
          {user && <button onClick={() => setOpenModal('story')} style={{...s.createBtn, backgroundColor:'#d4af37'}}>סטורי ✨</button>}
          {user && <button onClick={() => setView(view === 'home' ? 'channel' : 'home')} style={s.channelBtn}>{view === 'home' ? '👤 ערוץ' : '🏠 בית'}</button>}
        </div>
        <div style={s.authArea}>
          {!user ? <button onClick={loginWithGoogle} style={s.loginBtn}>כניסה</button> : 
          <div style={s.userContainer}><img src={user.photoURL} alt="" style={s.userAvatar} onClick={() => setView('channel')} /><button onClick={() => auth.signOut()} style={s.logoutBtn}>יציאה</button></div>}
        </div>
      </header>

      <main style={{...s.mainWrapper, paddingTop: isMobile ? '80px' : '105px'}}>
        {user?.uid === ADMIN_UID && view === 'home' && (
          <section style={s.adminPanel}>
              <div style={s.adminHeader}>
                 <h3 style={{margin:0}}>👑 ניהול אדמין</h3>
                 <div style={{display:'flex', gap:'8px'}}>
                    <input placeholder="רב חדש..." value={newCatName} onChange={e => setNewCatName(e.target.value)} style={s.adminInput}/>
                    <button onClick={() => {if(newCatName){addDoc(collection(db,"categories"),{name:newCatName}); setNewCatName('')}}} style={s.smallGoldBtn}>הוסף</button>
                 </div>
              </div>
              <div style={s.adminGrid}>
                 <div style={s.manageBox}>
                    <h4>⏳ אישורים ({pendingVideos.length + pendingStories.length})</h4>
                    <div style={s.pendingScroll}>
                       {pendingVideos.map(v => (<div key={v.id} style={s.adminRow}><span>🎥 {v.title}</span><button onClick={() => approveItem(v, 'video')} style={s.approveBtn}>אשר</button></div>))}
                       {pendingStories.map(st => (<div key={st.id} style={s.adminRow}><span>✨ סטורי</span><button onClick={() => approveItem(st, 'story')} style={s.approveBtn}>אשר</button></div>))}
                    </div>
                 </div>
                 <div style={s.manageBox}>
                    <h4>🏷️ קטגוריות</h4>
                    <div style={s.pendingScroll}>
                       {categories.map(c => (
                         <div key={c.id} style={s.adminRow}>
                           {editingCat?.id === c.id ? <input value={editingCat.name} onChange={e => setEditingCat({...editingCat, name: e.target.value})} onBlur={() => {updateDoc(doc(db,"categories",c.id),{name:editingCat.name}); setEditingCat(null)}} autoFocus style={s.smallInput}/> : 
                           <span onClick={() => setEditingCat(c)}>{c.name}</span>}
                           <button onClick={() => deleteDoc(doc(db,"categories",c.id))} style={s.delBtn}>🗑️</button>
                         </div>
                       ))}
                    </div>
                 </div>
              </div>
          </section>
        )}

        {view === 'home' ? (
          <>
            <div style={s.storyRow}>{stories.map(st => (<div key={st.id} style={s.storyBubble} onClick={() => setSelectedVideo(st)}><div style={s.storyCircle}><img src="/icon.png" alt="" style={s.storyImg} /></div><span style={s.storyLabel}>{st.category}</span></div>))}</div>
            <div style={s.controlsArea}>
              <input style={s.searchBar} placeholder="🔍 חפש שיעור..." onChange={(e) => setSearchTerm(e.target.value)} />
              <select style={s.sortDropdown} value={sortMethod} onChange={(e) => setSortMethod(e.target.value)}>
                <option value="newest">🆕 חדש ביותר</option>
                <option value="oldest">🕰️ ישן ביותר</option>
                <option value="az">🔤 א - ב</option>
                <option value="za">🔤 ב - א</option>
                <option value="views">🔥 הכי נצפים</option>
                <option value="likes">💖 הכי אהובים</option>
              </select>
            </div>
            <div style={s.chipContainer}>
              <button onClick={() => setSelectedFilter("הכל")} style={{...s.chipBtn, backgroundColor: selectedFilter === "הכל" ? '#0038b8' : '#f2f2f2', color: selectedFilter === "הכל" ? '#fff' : '#000'}}>הכל</button>
              {categories.map(cat => (<button key={cat.id} onClick={() => setSelectedFilter(cat.name)} style={{...s.chipBtn, backgroundColor: selectedFilter === cat.name ? '#0038b8' : '#f2f2f2', color: selectedFilter === cat.name ? '#fff' : '#000'}}>{cat.name}</button>))}
            </div>
            <div style={{...s.videoGrid, gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)'}}>
              {getSortedContent(videos).map(v => <VideoCard key={v.id} v={v} />)}
            </div>
          </>
        ) : (
          <div style={s.channelPage}>
              <div style={s.channelTabs}>
                <button onClick={() => setChannelTab('uploads')} style={{...s.tabBtn, borderBottom: channelTab === 'uploads' ? '3px solid #0038b8' : 'none'}}>העלאות שלי</button>
                <button onClick={() => setChannelTab('liked')} style={{...s.tabBtn, borderBottom: channelTab === 'liked' ? '3px solid #0038b8' : 'none'}}>אהבתי ❤️</button>
                <button onClick={() => setChannelTab('history')} style={{...s.tabBtn, borderBottom: channelTab === 'history' ? '3px solid #0038b8' : 'none'}}>היסטוריה</button>
              </div>
              <div style={{...s.videoGrid, gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)'}}>
                {getSortedContent(videos.filter(v => {
                  if(channelTab === 'uploads') return v.userId === user?.uid;
                  if(channelTab === 'liked') return (userData.liked || []).includes(v.id);
                  if(channelTab === 'history') return (userData.history || []).includes(v.id);
                  return false;
                })).map(v => <VideoCard key={v.id} v={v} />)}
              </div>
          </div>
        )}
      </main>

      {selectedVideo && (
        <div style={s.modalOverlay} onClick={() => setSelectedVideo(null)}>
          <div ref={modalContentRef} style={{...s.modalContent, width: isStoryMode ? (isMobile ? '100%' : '400px') : '100%', maxWidth: '1200px', height: isLandscape ? '100dvh' : 'auto'}} onClick={e => e.stopPropagation()}>
            <div style={{...s.cinemaBox, aspectRatio: isStoryMode ? '9/16' : '16/9', maxHeight: isLandscape ? '100dvh' : '85dvh'}}>
              <div id="yt-player-frame" style={s.iframeBase}></div>
              <div style={s.fullShield} onClick={() => isPlaying ? playerRef.current.pauseVideo() : playerRef.current.playVideo()}></div>
              <div style={s.customControls}>
                <input type="range" min="0" max="100" step="0.1" value={progress} onChange={(e) => playerRef.current.seekTo((e.target.value/100)*duration)} style={s.progressBar} />
                <div style={s.controlsRow}>
                  <div style={{display:'flex', gap:'10px', alignItems:'center'}}>
                    <button onClick={() => isPlaying ? playerRef.current.pauseVideo() : playerRef.current.playVideo()} style={s.playBtn}>{isPlaying ? '⏸' : '▶'}</button>
                    <span style={s.timeText}>{(currentTime/60).toFixed(0)}:{(currentTime%60).toFixed(0).padStart(2,'0')}</span>
                    <input type="range" min="0" max="100" value={volume} onChange={(e) => {setVolume(e.target.value); playerRef.current.setVolume(e.target.value)}} style={{width:'40px'}} />
                  </div>
                  <div style={{display:'flex', gap:'10px'}}>
                     <button onClick={() => { if(modalContentRef.current.requestFullscreen) modalContentRef.current.requestFullscreen(); }} style={s.fsBtn}>⛶</button>
                     <button onClick={(e) => handleLike(e, selectedVideo.id)} style={{...s.playerLike, color: (userData.liked || []).includes(selectedVideo.id) ? '#ff4b2b' : '#fff'}}>❤️</button>
                  </div>
                </div>
              </div>
            </div>
            <button style={s.closeBtn} onClick={() => setSelectedVideo(null)}>✕</button>
          </div>
        </div>
      )}

      {openModal && (
        <div style={s.modalOverlay} onClick={() => setOpenModal(null)}>
          <div style={s.uploadPopup} onClick={e => e.stopPropagation()}>
            <form onSubmit={async (e) => {
                e.preventDefault();
                const url = openModal === 'video' ? videoUrl : storyUrl;
                await addDoc(collection(db, openModal === 'video' ? "pending_videos" : "pending_stories"), {
                  url, 
                  title: openModal === 'video' ? fetchedTitle : "סטורי", 
                  category: targetCategory,
                  userId: user.uid, 
                  userName: user.displayName,
                  createdAt: serverTimestamp()
                });
                setOpenModal(null); setVideoUrl(''); setStoryUrl(''); setFetchedTitle('');
                alert("נשלח לאישור אדמין! ✨");
            }} style={s.popupForm}>
              <h3>{openModal === 'video' ? '🎥 וידאו חדש' : '✨ סטורי חדש'}</h3>
              <input 
                placeholder="הדבק קישור יוטיוב..." 
                value={openModal === 'video' ? videoUrl : storyUrl} 
                onChange={(e) => {
                  const val = e.target.value;
                  if(openModal === 'video') {
                    setVideoUrl(val);
                    fetch(`https://noembed.com/embed?url=${encodeURIComponent(val)}`)
                      .then(r => r.json())
                      .then(d => setFetchedTitle(d.title || "ללא כותרת"));
                  } else {
                    setStoryUrl(val);
                  }
                }} 
                style={s.popupInput} 
                required 
              />
              <select onChange={e => setTargetCategory(e.target.value)} style={s.popupInput} required>
                <option value="">בחר קטגוריה / רב</option>
                {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
              <button type="submit" style={s.popupSubmit}>שלח לאישור</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Styles --- (Keep exactly as your current CSS)
const s = {
  container: { direction: 'rtl', backgroundColor: '#fff', minHeight: '100vh', fontFamily: 'Arial' },
  header: { position: 'fixed', top: 0, left: 0, right: 0, backgroundColor: '#fff', display: 'flex', alignItems: 'center', padding: '0 15px', zIndex: 1000, borderBottom: '1px solid #eee', justifyContent: 'space-between' },
  actionButtons: { display: 'flex', gap: '5px' },
  adminPanel: { margin: '20px', padding: '15px', backgroundColor: '#f0f7ff', borderRadius: '15px', border: '1px solid #cce5ff' },
  adminHeader: { display: 'flex', justifyContent: 'space-between', marginBottom: '15px', alignItems: 'center' },
  adminGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' },
  manageBox: { backgroundColor: '#fff', padding: '12px', borderRadius: '10px', border: '1px solid #eee' },
  adminRow: { display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid #f9f9f9', fontSize:'12px', alignItems:'center' },
  approveBtn: { backgroundColor:'#2ba640', color:'#fff', border:'none', borderRadius:'5px', padding:'3px 8px', cursor:'pointer' },
  authArea: { display: 'flex', alignItems: 'center' },
  userContainer: { display: 'flex', alignItems: 'center', gap: '8px' },
  userAvatar: { width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer' },
  loginBtn: { padding: '8px 16px', backgroundColor: '#0038b8', color: '#fff', border: 'none', borderRadius: '20px', cursor: 'pointer', fontWeight:'bold', fontSize:'12px' },
  logoutBtn: { background: 'none', border: 'none', color: '#ff4b2b', fontSize: '11px', cursor: 'pointer' },
  createBtn: { padding: '8px 12px', backgroundColor: '#f2f2f2', color: '#333', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor:'pointer', fontSize:'11px' },
  channelBtn: { padding: '8px 12px', border: '1px solid #0038b8', color: '#0038b8', borderRadius: '10px', background:'none', cursor:'pointer', fontSize:'11px' },
  mainWrapper: { width: '100%' },
  controlsArea: { padding: '10px 20px', display:'flex', gap:'10px' },
  searchBar: { flex: 2, padding: '10px', borderRadius: '20px', border: '1px solid #ddd', backgroundColor: '#f9f9f9' },
  sortDropdown: { flex: 1, padding: '8px', borderRadius: '20px', border: '1px solid #ddd', fontSize:'12px' },
  chipContainer: { display: 'flex', gap: '10px', padding: '10px 20px', overflowX: 'auto' },
  chipBtn: { padding: '8px 18px', border: 'none', borderRadius: '20px', whiteSpace: 'nowrap', cursor:'pointer', fontSize:'13px' },
  videoGrid: { display: 'grid', gap: '20px', padding: '20px' },
  videoCard: { cursor: 'pointer', backgroundColor:'#fff', borderRadius:'12px', overflow:'hidden', boxShadow:'0 1px 3px rgba(0,0,0,0.1)' },
  videoWrapper: { position: 'relative', width: '100%', paddingTop: '56.25%', backgroundColor: '#000' },
  thumbnail: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' },
  videoInfo: { padding: '10px' },
  vTitle: { margin: '0 0 5px 0', fontSize: '13px', fontWeight: 'bold', height:'35px', overflow:'hidden' },
  vRabbi: { margin: 0, fontSize: '11px', color: '#0038b8', fontWeight: 'bold' },
  vStats: { margin: '5px 0 0 0', fontSize: '10px', color: '#999' },
  gridLike: { background:'none', border:'none', fontSize:'14px', cursor:'pointer' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.95)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modalContent: { position: 'relative', display:'flex', alignItems:'center', justifyContent:'center', backgroundColor: '#000' },
  cinemaBox: { position: 'relative', width: '100%', margin: 'auto' },
  iframeBase: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' },
  fullShield: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 2050 },
  customControls: { position: 'absolute', left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', padding: '10px 15px', zIndex: 2060, display: 'flex', flexDirection: 'column' },
  progressBar: { width: '100%', cursor: 'pointer', accentColor: '#0038b8' },
  controlsRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop:'5px' },
  playBtn: { background: 'none', border: 'none', color: '#fff', fontSize: '20px', cursor:'pointer' },
  timeText: { color: '#fff', fontSize: '11px', fontFamily:'monospace' },
  fsBtn: { background:'none', border:'none', color:'#fff', fontSize:'16px', cursor:'pointer' },
  playerLike: { background:'none', border:'none', fontSize:'14px', cursor:'pointer' },
  closeBtn: { position: 'absolute', top: '10px', left: '10px', border: 'none', background: 'white', borderRadius: '50%', width: '30px', height: '35px', zIndex: 2100, cursor:'pointer' },
  uploadPopup: { backgroundColor: '#fff', padding: '20px', borderRadius: '15px', width: '90%', maxWidth: '400px' },
  popupForm: { display: 'flex', flexDirection: 'column', gap: '15px' },
  popupInput: { padding: '10px', borderRadius: '10px', border: '1px solid #ddd' },
  popupSubmit: { padding: '10px', backgroundColor: '#0038b8', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor:'pointer' },
  channelPage: { padding: '20px' },
  channelTabs: { display:'flex', gap:'20px', marginBottom:'20px', borderBottom:'1px solid #eee' },
  tabBtn: { padding:'10px 15px', background:'none', border:'none', fontWeight:'bold', cursor:'pointer', fontSize:'14px' },
  smallInput: { padding:'3px', borderRadius:'5px', border:'1px solid #ddd', fontSize:'11px' },
  smallGoldBtn: { backgroundColor:'#d4af37', color:'#fff', border:'none', borderRadius:'8px', padding:'5px 15px', fontWeight:'bold', cursor:'pointer', fontSize:'11px' },
  delBtn: { background:'none', border:'none', cursor:'pointer', fontSize:'14px' },
  storyRow: { display: 'flex', gap: '15px', padding: '15px 20px', overflowX: 'auto' },
  storyBubble: { display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth:'70px', cursor:'pointer' },
  storyCircle: { width: '60px', height: '60px', borderRadius: '50%', border: '3px solid #d4af37', padding: '2px' },
  storyImg: { width: '100%', height: '100%', borderRadius: '50%', objectFit: 'contain' },
  storyLabel: { fontSize: '10px', marginTop: '5px' },
  pendingScroll: { maxHeight:'200px', overflowY:'auto' },
  adminInput: { padding:'8px', borderRadius:'8px', border:'1px solid #ddd' }
};

export default App;